import { useEffect, useState } from 'react';
import type { CalendarDateTime } from '@internationalized/date';
import {
  Button,
  Description,
  Drawer,
  Separator,
  type OverlayTriggerState,
} from '@heroui/react';
import type { BulkRequestPayload } from '../../../api/available-spots';
import { AsyncButton } from '../../../components/ui/async-button';
import { InlineDateTimeSection } from '../../../components/ui/inline-datetime-section';
import {
  drawerBodyClassName,
  drawerFooterClassName,
  sideDrawerDialogClassName,
} from '../../../components/ui/side-drawer-classes';
import { StatusCallout } from '../../../components/ui/status-callout';
import { UsageTypeRadioGroup } from '../../../components/ui/usage-type-radio-group';
import { getErrorMessage } from '../../../lib/toast';
import type { UsageType } from '../../../api/my-parking-spots';
import {
  AvailabilityDurationPresets,
  type AvailabilityDurationPresetId,
} from '../../my-parking-spots/components/availability-duration-presets';
import {
  addHoursToCalendarDateTime,
  addMinutesToCalendarDateTime,
  calendarDateTimeToIsoUtc,
  compareCalendarDateTime,
  getCalendarEndWhenStartChanges,
  getCurrentCalendarDateTime,
} from '../../../utils/date-utils';

interface BulkRequestDrawerProps {
  state: OverlayTriggerState;
  onSubmit: (payload: BulkRequestPayload) => Promise<void>;
  variant?: 'bulk' | 'spot';
  spotCode?: string;
}

export function BulkRequestDrawer({
  state,
  onSubmit,
  variant = 'bulk',
  spotCode,
}: BulkRequestDrawerProps) {
  const [startAt, setStartAt] = useState<CalendarDateTime | null>(null);
  const [endAt, setEndAt] = useState<CalendarDateTime | null>(null);
  const [type, setType] = useState<UsageType>('BORROW');
  const [selectedPresetId, setSelectedPresetId] = useState<AvailabilityDurationPresetId | null>(
    '1h',
  );
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!state.isOpen) return;
    const start = addMinutesToCalendarDateTime(getCurrentCalendarDateTime(), 5);
    setStartAt(start);
    setEndAt(addHoursToCalendarDateTime(start, 1));
    setType('BORROW');
    setSelectedPresetId('1h');
    setError(null);
    setSubmitting(false);
  }, [state.isOpen]);

  const handleSubmit = async () => {
    if (!startAt || !endAt) {
      setError('Data de início e fim são obrigatórias.');
      return;
    }

    const minStart = addMinutesToCalendarDateTime(getCurrentCalendarDateTime(), 1);
    if (compareCalendarDateTime(startAt, minStart) < 0) {
      setError('A data e hora de início deve ser pelo menos 1 minuto no futuro.');
      return;
    }

    const minEnd = addMinutesToCalendarDateTime(startAt, 1);
    if (compareCalendarDateTime(endAt, minEnd) < 0) {
      setError('A data e hora de fim deve ser pelo menos 1 minuto depois da data de início.');
      return;
    }

    setSubmitting(true);
    setError(null);
    try {
      await onSubmit({
        startAt: calendarDateTimeToIsoUtc(startAt),
        endAt: calendarDateTimeToIsoUtc(endAt),
        type,
      });
      state.close();
    } catch (err) {
      setError(getErrorMessage(err, 'Erro ao enviar o pedido.'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Drawer state={state}>
      <Drawer.Backdrop isDismissable>
        <Drawer.Content placement="right">
          <Drawer.Dialog className={sideDrawerDialogClassName}>
            <Drawer.Header>
              <Drawer.Heading>
                {variant === 'spot' && spotCode
                  ? `Solicitar vaga ${spotCode}`
                  : 'Solicitação em massa'}
              </Drawer.Heading>
              <Drawer.CloseTrigger />
            </Drawer.Header>

            <Drawer.Body className={drawerBodyClassName}>
              <Description>
                {variant === 'spot'
                  ? 'Envie um pedido só para o proprietário desta vaga, mesmo ela não estando disponível agora.'
                  : 'Envie um pedido para todos os proprietários de vagas do condomínio.'}
              </Description>

              <StatusCallout status="info" title="Como funciona">
                {variant === 'spot'
                  ? 'O morador recebe o pedido e pode liberar a vaga para o período. Sem resposta, o pedido vale por 24 horas.'
                  : 'Os proprietários recebem o pedido. Se alguém aprovar, o uso é confirmado. Sem aprovação, a solicitação expira em 24 horas.'}
              </StatusCallout>

              {error ? <StatusCallout status="danger">{error}</StatusCallout> : null}

              <AvailabilityDurationPresets
                title="Solicitar por"
                description="A partir de agora, pela duração escolhida."
                selectedPresetId={selectedPresetId}
                startAt={startAt}
                endAt={endAt}
                onSelectPreset={(presetId, nextStart, nextEnd) => {
                  setSelectedPresetId(presetId);
                  setStartAt(nextStart);
                  setEndAt(nextEnd);
                  setError(null);
                }}
              />

              <div className="flex items-center gap-3">
                <Separator className="flex-1" />
                <span className="text-xs uppercase tracking-wide text-muted">ou</span>
                <Separator className="flex-1" />
              </div>

              <Description className="text-xs text-muted">
                Escolha início e fim manualmente nos calendários abaixo.
              </Description>

              <div className="space-y-6">
                <InlineDateTimeSection
                  title="Data e hora de início"
                  value={startAt}
                  minValue={getCurrentCalendarDateTime()}
                  onChange={(value) => {
                    setSelectedPresetId(null);
                    setStartAt(value);
                    setEndAt((prev) => getCalendarEndWhenStartChanges(value, prev));
                  }}
                />

                <InlineDateTimeSection
                  title="Data e hora de fim"
                  value={endAt}
                  minValue={
                    startAt
                      ? addMinutesToCalendarDateTime(startAt, 1)
                      : getCurrentCalendarDateTime()
                  }
                  onChange={(value) => {
                    setSelectedPresetId(null);
                    setEndAt(value);
                  }}
                />

                <UsageTypeRadioGroup
                  label="Tipo de uso"
                  description="Selecione o tipo de vaga que deseja solicitar."
                  value={type}
                  onChange={setType}
                  isDisabled={submitting}
                />
              </div>
            </Drawer.Body>

            <Drawer.Footer className={drawerFooterClassName}>
              <Button type="button" variant="secondary" onPress={() => state.close()}>
                Fechar
              </Button>
              <AsyncButton
                type="button"
                variant="primary"
                isPending={submitting}
                isDisabled={!startAt || !endAt}
                onPress={handleSubmit}
              >
                {variant === 'spot' ? 'Enviar pedido' : 'Enviar solicitações'}
              </AsyncButton>
            </Drawer.Footer>
          </Drawer.Dialog>
        </Drawer.Content>
      </Drawer.Backdrop>
    </Drawer>
  );
}
